import React, { useState } from "react";
import { toast } from "react-toastify";

import { getDocumentFileBlobApi } from "../api/documentsApi";

type Props = {
  token: string;
  /** Documento do colaborador; ou `fetchBlob` para outro arquivo do DP (atestado, aviso de férias). */
  documentId?: string;
  fetchBlob?: () => Promise<Blob>;
  fileName?: string | null;
  label?: string;
  /** Classe do botão; sem ela, o tamanho padrão das listas de documentos. */
  className?: string;
  disabled?: boolean;
  /** Dica do botão (padrão: o nome do arquivo). */
  title?: string;
};

/**
 * Abre o arquivo do documento. Ele só sai pela rota do DP (ADMIN e DP), com o
 * token do staff — um link direto não mandaria o Authorization. Por isso o
 * arquivo vem como blob e vira object URL, aberto numa aba nova (PDF e imagem
 * o navegador exibe; o botão de download do visualizador usa o nome original).
 */
export default function DocumentFileButton({
  token,
  documentId,
  fetchBlob,
  fileName,
  label = "Abrir",
  className,
  disabled,
  title,
}: Props) {
  const [busy, setBusy] = useState(false);

  const open = async () => {
    // A aba é aberta ANTES do await: aberta depois, o bloqueador de pop-up a barra.
    const tab = window.open("", "_blank");
    setBusy(true);
    try {
      const blob = fetchBlob ? await fetchBlob() : await getDocumentFileBlobApi(token, documentId as string);
      const url = URL.createObjectURL(blob);
      if (tab) {
        tab.location.href = url;
      } else {
        // Sem aba (bloqueada mesmo assim): baixa com o nome original.
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName || "documento";
        link.click();
      }
      // Tempo para a aba carregar antes de liberar a memória.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error: any) {
      tab?.close();
      toast.error(error?.message || "Erro ao abrir o arquivo.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      className={className ?? "dpBtn dpBtn--secondary"}
      style={className ? undefined : { height: 30, padding: "0 10px" }}
      onClick={open}
      disabled={busy || disabled}
      title={title ?? (fileName || undefined)}
    >
      {busy ? "Abrindo..." : label}
    </button>
  );
}
